/* Noetic: muted gold review stars across Growave widgets.
 * Reversible: remove this asset's script tag and restore settings.star_color.
 * Gold rating color and sharp star corners; full/half/empty values stay intact.
 */
(() => {
  'use strict';
  const styleId = 'noetic-black-review-stars';
  const css = ':host, [style*="--gw-rv-icon-starColor"] { --gw-rv-icon-starColor: #a67c32 !important; }';
  const hostSelector = '[class*="gw-"][class*="placeholder"], [id^="gw-"], [id*="growave"], [class*="growave"]';

  const starSelector = 'svg[class*="gw-rv-avg-rating__star"], svg[class*="gw-rv-rating-stars__star"]';
  const sharpStarPath = 'M10 1.5L12.63 6.83L18.5 7.68L14.25 11.82L15.25 17.67L10 14.9L4.75 17.67L5.75 11.82L1.5 7.68L7.37 6.83Z';
  const sharpHalfPath = 'M10 1.5L7.37 6.83L1.5 7.68L5.75 11.82L4.75 17.67L10 14.9Z';

  function setSvgAttributes(node, values) {
    for (const [name, value] of Object.entries(values)) {
      if (node.getAttribute(name) !== value) node.setAttribute(name, value);
    }
  }

  function sharpenStar(svg) {
    if (!svg.matches(starSelector) || svg.getAttribute('viewBox') !== '0 0 20 20') return;
    const paths = [...svg.querySelectorAll('path')];
    if (!paths.length) return;
    // Listing-average hosts belong only to product cards, including recommendations.
    const productCard = svg.getRootNode().host?.classList.contains('gw-rv-listing-average-placeholder');
    const reduction = productCard ? 4 : 2;
    // Keep the native SVG dimensions as a stable baseline; never subtract again.
    for (const dimension of ['width', 'height']) {
      const originalSize = Number(svg.getAttribute(dimension));
      if (Number.isFinite(originalSize) && originalSize > reduction) {
        const reducedSize = (originalSize - reduction) + 'px';
        if (svg.style.getPropertyValue(dimension) !== reducedSize) {
          svg.style.setProperty(dimension, reducedSize);
        }
      }
    }
    const half = svg.classList.contains('gw-rv-avg-rating__star-half') || svg.classList.contains('gw-rv-rating-stars__star-half');
    const empty = svg.classList.contains('gw-rv-avg-rating__star-empty') || svg.classList.contains('gw-rv-rating-stars__star-empty');
    paths.forEach((path, index) => {
      const outline = (half || empty) && index === 0;
      setSvgAttributes(path, {
        d: half && index > 0 ? sharpHalfPath : sharpStarPath,
        fill: outline ? 'none' : 'currentColor',
        stroke: outline ? 'currentColor' : 'none',
        'stroke-width': outline ? '1.15' : '0',
        'stroke-linejoin': 'miter',
        'stroke-linecap': 'butt'
      });
    });
    setSvgAttributes(svg, { 'data-noetic-sharp-star': half ? 'half' : empty ? 'empty' : 'full' });
  }

  function sharpenStars(node) {
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    const parentSvg = node.nodeType === 1 ? node.closest('svg') : null;
    if (parentSvg) sharpenStar(parentSvg);
    for (const svg of node.querySelectorAll(starSelector)) sharpenStar(svg);
  }

  const visiblePending = new Set();
  const roots = new WeakSet();
  let retryTimer;

  // Lazy widgets can attach shadow roots long after page load. Poll only
  // visible pending hosts, and stop as soon as they mount or leave the viewport.
  const visibility = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.target.isConnected) {
        if (!decorate(entry.target)) visiblePending.add(entry.target);
      } else {
        visiblePending.delete(entry.target);
      }
    }
    scheduleRetries();
  }, { rootMargin: '300px' });

  function decorate(host) {
    const root = host.shadowRoot;
    if (!root) return false;
    visiblePending.delete(host);
    visibility.unobserve(host);
    if (!root.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = css;
      root.append(style);
    }
    sharpenStars(root);
    watch(root);
    return true;
  }

  function scheduleRetries() {
    if (!visiblePending.size || retryTimer) return;
    retryTimer = setInterval(() => {
      for (const host of visiblePending) {
        if (!host.isConnected || decorate(host)) visiblePending.delete(host);
      }
      if (!visiblePending.size) {
        clearInterval(retryTimer);
        retryTimer = undefined;
      }
    }, 500);
  }

  function discover(node) {
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    const candidates = [];
    if (node.nodeType === 1 && node.matches(hostSelector)) candidates.push(node);
    candidates.push(...node.querySelectorAll(hostSelector));
    for (const host of candidates) {
      if (!decorate(host)) visibility.observe(host);
    }
  }

  function watch(root) {
    if (roots.has(root)) return;
    roots.add(root);
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') {
          const svg = record.target.closest('svg');
          if (svg) sharpenStar(svg);
          continue;
        }
        for (const node of record.addedNodes) {
          discover(node);
          sharpenStars(node);
        }
      }
      if (root.host && !root.getElementById(styleId)) decorate(root.host);
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['d', 'class'] });
    discover(root);
  }

  watch(document.documentElement);
  document.addEventListener('shopify:section:load', event => discover(event.target));
})();
